// @flow

import React, {type Node} from 'react';
import {connect} from 'react-redux';

import type {StoredResponseType} from 'app/utils/redux_v2';
import type {StoreShapeType} from 'app/reducers/types';
import {deepDiffer} from 'app/utils/react-utils';
import {withLoadingIndicator} from 'app/general/sweet-loading-bar';

/*
    "multiple" data fetcher
    allow multiple params and responses - useful for incremental pagination
    where the results of the pages are shown concatenated
 */

type OwnProps<I> = {|
  skipInitialFetch?: boolean,
  hideLoadingIndicator?: boolean,
  denormalizeFunc: (StoreShapeType, {[string]: any}) => StoredResponseType<I>,
  fetchDataSource: ({[string]: any}) => Promise<any>,
  fetchParamsArray: Array<{[string]: any}>,
  children: (Array<StoredResponseType<I>>, () => Promise<any>) => Node,
  onFetch?: (Array<StoredResponseType<I>>) => any,
|};

type StateProps<I> = {|
  responses: Array<StoredResponseType<I>>,
|};

type DispatchProps = {|
  fetchData: (Array<{[string]: any}>) => Promise<any>,
|};

type Props<I> = {|
  ...OwnProps<I>,
  ...StateProps<I>,
  ...DispatchProps,
|};

class BaseMultipleStoredDataFetcher<I> extends React.PureComponent<Props<I>> {
  componentDidMount() {
    const {skipInitialFetch, fetchData, fetchParamsArray} = this.props;

    if (!skipInitialFetch) {
      fetchData(fetchParamsArray);
    }
  }

  componentDidUpdate(prevProps: Props<I>) {
    const {fetchParamsArray, fetchData, onFetch, responses} = this.props;

    const toFetch = fetchParamsArray.filter((params) => {
      return (
        prevProps.fetchParamsArray.filter(
          (oldParams) => !deepDiffer(params, oldParams)
        ).length === 0
      );
    });

    if (toFetch.length) {
      fetchData(toFetch);
    }

    if (onFetch != null && deepDiffer(prevProps.responses, responses)) {
      onFetch(responses);
    }
  }

  refetchData = () => {
    const {fetchParamsArray, fetchData} = this.props;
    return fetchData(fetchParamsArray);
  };

  render() {
    const {responses, children} = this.props;
    return children(responses, this.refetchData);
  }
}

const mapStateToProps = (
  state,
  {fetchParamsArray, denormalizeFunc}
): StateProps<any> => ({
  responses: fetchParamsArray.map((fetchParams) =>
    denormalizeFunc(state, fetchParams)
  ),
});

const mapDispatchToProps = (
  dispatch,
  {fetchDataSource, hideLoadingIndicator}: OwnProps<any>
): DispatchProps => ({
  fetchData: (params) =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        const fetchData = () =>
          Promise.all(
            params.map((param) => dispatch(fetchDataSource(param)))
          ).then(resolve, reject);

        return hideLoadingIndicator
          ? fetchData()
          : dispatch(withLoadingIndicator(fetchData));
      }, 0);
    }),
});

export const MultipleStoredDataFetcher = connect<
  Props<any>,
  OwnProps<any>,
  StateProps<any>,
  DispatchProps,
  StoreShapeType,
  _
>(
  mapStateToProps,
  mapDispatchToProps
)(BaseMultipleStoredDataFetcher);
